
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * AetTimezone
 *
 * @file AetTimezone.jsx
 * @author Anuja Gaikwad
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Select,
  MenuItem,
  FormControl,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import mixins from '../../assets/styles/mixins.scss';
import CommonStyles from '../../assets/styles/CommonStyles';

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    minWidth: 0
  },

  label: {
    fontSize: 14,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.greyScaleColor,
    lineHeight: '22px',
    whiteSpace: 'nowrap'
  },

  formControl: {
    width: '100%'
  },

  selectRoot: {
    height: 48,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    cursor: 'pointer',

    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      flex: 1,
      minWidth: 0,
      cursor: 'pointer'
    },
    '& .MuiSelect-selectMenu': {
      fontSize: mixins.fontSize16,
      fontWeight: mixins.fontWeight400,
      fontFamily: mixins.fontFamilyPlusSans,
      color: mixins.greyScaleColor,
      lineHeight: 24,
      maxHeight: 48,
      padding: '0px 12px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst,
      borderWidth: 1
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: mixins.chalkWhiteFont
    }
  },

  selectValueRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0,
    gap: 8
  },
  selectValueText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  selectValueIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
  },
  menuPaper: {
    ...CommonStyles().scrollContainer({
      scrollbarWidth: 8,
      scrollbarColor: 'rgba(81, 46, 171, 0.15) transparent',
      borderRadius: 4
    })
  },

  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 10,
    whiteSpace: 'normal'
  },

  menuItemName: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.timeIconColor,
    lineHeight: '24px',
    wordBreak: 'break-word'
  },

  menuItemOffset: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '24px'
  },
  timezonePopover: {
    marginTop: '8px'
  }
}));

const AetTimezone = ({
  label,
  value,
  onChange,
  icons,
  options
}) => {
  const classes = useStyles();

  return (
    <div
      className={classes.wrapper}
    >
      {label && (
        <Typography className={classes.label}>
          {label}
        </Typography>
      )}

      <FormControl
        variant="outlined"
        className={classes.formControl}
      >
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={classes.selectRoot}
          IconComponent={() => null} // removes default arrow
          renderValue={(selected) => {
            const selectedVal = options.find(o => o.value === selected);
            const text = selectedVal ? `${selectedVal.abbr} (${selectedVal.offset})` : selected;

            if (!icons?.timeZone) return text;

            // put the icon inside select row so it is clickable
            return (
              <span className={classes.selectValueRow}>
                <span className={classes.selectValueText}>{text}</span>
                <span className={classes.selectValueIcon} aria-hidden>
                  {icons.timeZone}
                </span>
              </span>
            );
          }}
          MenuProps={{
            PopoverClasses: {
              root: classes.timezonePopover
            },
            PaperProps: {
              className: classes.menuPaper,
              style: {
                width: 'var(--timezone-field-width, 216px)',
                maxHeight: '55vh'
              }
            },
            MenuListProps: {
              style: {
                paddingTop: 0,
                paddingBottom: 0
              }
            },
            TransitionProps: {
              onEntering: (element) => {
                const selectedItem = element.querySelector(
                  '[aria-selected="true"]'
                );

                if (selectedItem) {
                  selectedItem.scrollIntoView({
                    block: 'center'
                  });
                }
              }
            },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            getContentAnchorEl: null,
            disablePortal: true
          }}
        >
          {options.map((item) => (
            <MenuItem key={item.value} value={item.value} className={classes.menuItem}>
              <span className={classes.menuItemName}>{item.name}</span>
              <span className={classes.menuItemOffset}>{item.offset}</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

AetTimezone.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  icons: PropTypes.node,
  options: PropTypes.array.isRequired
};

AetTimezone.defaultProps = {
  label: '',
  icons: null
};

export default AetTimezone;
